
import { useMeToday } from "@/hooks/use-me-queries";
import { getMeSession } from "@/lib/auth/storage";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { formatDateDisplay, formatTimeIST, todayIST } from "@/lib/timezone";
import { CATEGORY_LABELS } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { ApiError } from "@/lib/api/client";

export default function MeTodayPage() {
  const session = getMeSession();
  const { data, isLoading, error } = useMeToday();

  if (!session) {
    return null;
  }

  if (isLoading) {
    return (
      <p className="text-center text-muted-foreground">Loading today…</p>
    );
  }

  if (error) {
    const isNotFound =
      error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-5">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Today</p>
          <h2 className="text-2xl font-semibold">
            {formatDateDisplay(todayIST())}
          </h2>
          <p className="mt-2 text-lg">Namaste, {session.name}</p>
        </div>
        <Card className="border-saffron-100">
          <CardContent className="pt-6 text-center text-sm text-muted-foreground">
            {isNotFound ? (
              <>
                You&apos;re signed in. Today&apos;s attendance endpoint is not
                available on the server yet — check back after your coordinator
                enables <code className="text-xs">/biometric/me/today</code>.
              </>
            ) : (
              <>Could not load today&apos;s status. Please try again shortly.</>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-center text-red-600">
        Could not load today&apos;s status.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-sm text-muted-foreground">Today</p>
        <h2 className="text-2xl font-semibold">
          {formatDateDisplay(data.date)}
        </h2>
        <div className="mt-2 flex flex-wrap justify-center gap-1">
          {(data.categories ?? session.person_categories).map((c) => (
            <Badge key={c} variant="secondary">
              {CATEGORY_LABELS[c] ?? c}
            </Badge>
          ))}
        </div>
      </div>

      {!data.morning_finalized && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm text-slate-600">
          Morning status finalizes at 7:00 AM IST if you haven&apos;t punched.
        </p>
      )}

      <div className="space-y-3">
        {data.sessions.map((sessionRow) => (
          <Card key={sessionRow.session} className="border-saffron-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{sessionRow.label}</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <AttendanceCellBadge
                cell={{
                  status: sessionRow.status,
                  late_minutes: sessionRow.late_minutes,
                  first_punch_at: sessionRow.first_punch_at,
                }}
              />
              {sessionRow.first_punch_at && (
                <p className="text-sm text-muted-foreground">
                  {formatTimeIST(sessionRow.first_punch_at)}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {data.sessions.length === 0 && (
        <p className="text-center text-muted-foreground">
          No sessions scheduled for you today.
        </p>
      )}
    </div>
  );
}
